import React, { useEffect, useState } from "react";
import { GoogleMap, Circle, Polygon } from "@react-google-maps/api";
import { Button } from "../ui/button";

interface Point {
  lat: number;
  lng: number;
}

interface PointMapComponentProps {
  regions: string[];
  setRegions: React.Dispatch<React.SetStateAction<string[]>>;
  isEditing: boolean;
}

const PointMapComponent: React.FC<PointMapComponentProps> = ({
  regions,
  setRegions,
  isEditing,
}) => {
  const [center, setCenter] = useState<Point | null>(null);
  const [polygonCoords, setPolygonCoords] = useState<Point[]>([]);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  useEffect(() => {
    const getUserLocation = async () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setCenter(userLocation);
          },
          () => {
            setCenter({ lat: 39.8283, lng: -98.5795 });
          }
        );
      } else {
        setCenter({ lat: 39.8283, lng: -98.5795 });
      }
    };

    getUserLocation();
  }, []);

  useEffect(() => {
    if (regions.length > 0) {
      const newCoords = regions.map((region) => {
        const [lat, lng] = region.split(", ").map(Number);
        return { lat, lng };
      });
      setPolygonCoords(newCoords);
    }
  }, [regions]);

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (isEditing && e.latLng) {
      const newPoint = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      setPolygonCoords((prevCoords) => [...prevCoords, newPoint]);
      setRegions((prevRegions) => [
        ...prevRegions,
        `${newPoint.lat}, ${newPoint.lng}`,
      ]);
    }
  };

  const handleRemoveLastPoint = () => {
    setPolygonCoords((prevCoords) => prevCoords.slice(0, -1));
    setRegions((prevRegions) => prevRegions.slice(0, -1));
  };

  const handleRemoveRegion = () => {
    setPolygonCoords([]);
    setRegions([]);
  };

  const handleMapLoad = () => {
    setIsMapLoaded(true);
  };

  return (
    <div>

      {isEditing && polygonCoords.length === 0 && (
        <p className="text-gray-500  text-sm italic mb-1">
          * Draw the region on the map
        </p>
      )}

      {center && (
        <GoogleMap
          center={center}
          zoom={12}
          options={{
            disableDefaultUI: true,
          }}
          mapContainerStyle={{
            width: "100%",
            height: "350px",
          }}
          onClick={handleMapClick}
          onLoad={handleMapLoad}
        >
          <Polygon
            paths={polygonCoords}
            options={{
              fillColor: "#87CEFA",
              fillOpacity: 0.4,
              strokeColor: "#4682B4",
              strokeOpacity: 0.8,
              strokeWeight: 2,
            }}
          />
          {polygonCoords.map((coord, index) => (
            <Circle
              key={index}
              center={coord}
              radius={50}
              options={{
                fillColor: "#20B2AA",
                fillOpacity: 0.8,
                strokeColor: "#5F9EA0",
                strokeOpacity: 0.8,
                strokeWeight: 2,
              }}
            />
          ))}
        </GoogleMap>
      )}
      {isEditing && polygonCoords.length > 0 && isMapLoaded &&(
        <div className="mt-[10px] flex max-w-full justify-between">
          <Button
            variant="outline"
            type="button"
            onClick={handleRemoveLastPoint}
          >
            Delete the last point
          </Button>
          <Button variant="outline" type="button" onClick={handleRemoveRegion}>
            Delete Region
          </Button>
        </div>
      )}
    </div>
  );
};

export default PointMapComponent;
